import 'vue-multiselect/dist/vue-multiselect.min.css';

import {PROTOCOLS_CSV} from '@/../configs/protocolsCsv';
import isEmpty from "@/libs/isEmpty";
import {transformMultiSelect} from "@/libs/transformData/transformMultiSelect";

import VeeValidate from 'vee-validate';

import ICity from '@/interfaces/cities/ICity';
import ICountry from '@/interfaces/countries/ICountry';
import IDataCenter from '@/interfaces/dataCenter/IDataCenter';
import IAccessLevel from '@/interfaces/IAccessLevel';
import IPayloadQuery from "@/interfaces/IPayloadQuery";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import IVpn from '@/interfaces/vpns/IVpn';
import capitalizeFirstLetter from "@/libs/capitalizeFirstLetter";
import cloneObject from "@/libs/cloneObject";
import {Switch as cSwitch} from '@coreui/vue';
import Multiselect from 'vue-multiselect';
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';

import Loader from '@/components/Includes/Loader/Loader.vue'

Vue.use(VeeValidate, {
    inject: true,
    fieldsBagName: 'veeFields'
});

@Component({
    name: 'VpnForm',
    components: {
        Multiselect,
        Loader,
        cSwitch
    }
})
export default class VpnForm extends Vue {
    @Prop(Object) private vpnData!: IVpn;
    @Prop(Boolean) private isEdit!: boolean;

    // Begin Vuex Logic for Access Level VPN
    @Action private accessLevelsList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private getAccessLevels!: () => IAccessLevel[];
    // End Vuex Logic for Access Level VPN

    // Begin Vuex Logic for Cities
    @Action private citiesList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private getCities!: () => ICity[];
    // End Vuex Logic for Cities

    // Begin Vuex Logic for Countries
    @Action private countriesList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private getCountries!: () => ICountry[];
    // End Vuex Logic for Countries

    // Begin Vuex Logic for Data Centers
    @Action private dataCentersList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private getDataCenters!: () => IDataCenter[];
    // End Vuex Logic for Data Centers

    private loading: boolean = true;


    private privacyTypes: IMultiSelectItem[] = [
        {
            label: 'freedom',
            value: 'freedom'
        },
        {
            label: 'usual',
            value: 'usual'
        }
    ];

    private get protocolsCsvOptions() {
        return PROTOCOLS_CSV.map(p => ({text: p, value: p}));
    }

    private get form(): IVpn {
        return this.vpnData;
    }

    private get isCreateForm() {
        return this.$route.name === 'vpn_create';
    }

    private get btnText() {
        return this.isCreateForm ? 'Create' : 'Update';
    }

    private get accessLevelsOptions() {
        return transformMultiSelect(this.accessLevels);
    }

    private get countriesOptions() {
        return transformMultiSelect(this.getCountries());
    }

    private get dataCentersOptions() {
        return transformMultiSelect(this.getDataCenters());
    }

    private get citiesOptions() {
        return transformMultiSelect(this.getCities());
    }

    private get accessLevels(): IAccessLevel[] {
        return this.getAccessLevels();
    }

    private get cities(): ICity[] {
        return this.getCities();
    }

    private get dataCenters(): IDataCenter[] {
        return this.getDataCenters();
    }

    private get countries(): ICountry[] {
        return this.getCountries();
    }

    private transformLabel(nameField: string): string {
        return capitalizeFirstLetter(nameField);
    }

    // Begin Handler by Form
    private checkDescription(field: string): string {
        const mainText: string = 'Example: ';

        switch (field) {
            case 'ip':
                return mainText + '127.0.0.1';
            case 'host':
                return mainText + 'https://hostname.com';
            default:
                return '';

        }
    }

    private checkRequired(field: string): string {
        const requiredFields = [
            'ip',
            'host',
            'dataCenter',
            'country',
            'city',
            'accessLevel'
        ];

        return field === 'ip' ? 'required|ip' : (requiredFields.indexOf(field) !== -1 ? 'required' : 'min:1');
    }

    private isCannotEdit(field: string): boolean {
        return field === 'createdAt' || field === 'updatedAt' || field === 'id' || field === 'alive';
    }

    private disabledField(field: string): boolean {
        if (field === 'autoCreated') {
            return !this.isCreateForm;
        }

        return !this.isCreateForm && !this.isCannotEdit(field) ? !this.isEdit && !this.isCannotEdit(field) : this.isCannotEdit(field)
    }

    private vpnDelete() {
        this.$emit('deleteVpn', this.form.id)
    }

    private handleSave() {
        this.validateForm();
    }

    private invalidValue(fieldName: string) {
        return this.$validator.errors.has(fieldName);
    }

    private validateForm() {
        this.$validator.validateAll().then((result) => {
            if (result) {
                this.saveBettingTip();
            }
        });
    }

    private saveBettingTip(): void {
        delete this.form.createdAt;
        delete this.form.updatedAt;

        const prefix: string = '/api/vpn';

        const formData = cloneObject(this.form);
        formData.privacyType = formData.privacyType.value;
        formData.dataCenter = `${prefix}/data_centers/${formData.dataCenter.value}`;
        formData.city = `${prefix}/cities/${formData.city.value}`;
        formData.country = `${prefix}/countries/${formData.country.value}`;
        formData.accessLevel = `${prefix}/access_levels/${formData.accessLevel.value}`;
        this.isCreateForm ? this.$emit('createVpn', formData) : this.$emit('updateVpn', formData)
    }

    private cancelForm(): void {
        this.$router.go(-1);
    }

    // End Handler by Form

    // Begin Relation logic
    private findId(data: any, source: any) {
        const sourceIsString = typeof source === 'string';

        const item = data.find((el: any) => sourceIsString ? (el.value === source) : (el.id === source.id));

        if(sourceIsString){
            return item
        } else {
            return {
                label: item.name,
                value: item.id
            }
        }
    }

    @Watch('accessLevels', { immediate: true })
    private checkAccessLevel() {
        if (this.accessLevels.length > 0) {
            const relationFullData = this.findId(this.accessLevels, this.vpnData.accessLevel);
            this.vpnData.accessLevel = relationFullData ? relationFullData : this.vpnData.accessLevel;
        }
    }

    @Watch('cities', { immediate: true })
    private checkCity() {
        if (this.cities.length > 0) {
            const relationFullData = this.findId(this.cities, this.vpnData.city);
            this.vpnData.city = relationFullData ? relationFullData : this.vpnData.city;
        }
    }

    @Watch('dataCenters', { immediate: true })
    private checkDataCenters() {
        if (this.dataCenters.length > 0) {
            const relationFullData = this.findId(this.dataCenters, this.vpnData.dataCenter);
            this.vpnData.dataCenter = relationFullData ? relationFullData : this.vpnData.dataCenter;
        }
    }

    @Watch('countries', { immediate: true })
    private checkCountries() {
        if (this.countries.length > 0) {
            const relationFullData = this.findId(this.countries, this.vpnData.country);
            this.vpnData.country = relationFullData ? relationFullData : this.vpnData.country;
        }
    }

    @Watch('privacyTypes', { immediate: true })
    private checkPrivacyType() {
        if (this.privacyTypes.length > 0) {
            const relationFullData = this.findId(this.privacyTypes, this.vpnData.privacyType);
            this.vpnData.privacyType = relationFullData ? relationFullData : this.vpnData.privacyType;
        }
    }

    private async getRelations(): Promise<void> {
        this.loading = true;
        
        try {     
            const dependencies = [];

            if (isEmpty(this.accessLevels)) {
                dependencies.push(this.accessLevelsList({query: '?pagination=false'}));
            }
    
            if (isEmpty(this.dataCenters)) {
                dependencies.push(this.dataCentersList({query: '?pagination=false'}));
            }
            if (isEmpty(this.countries)) {
                dependencies.push(this.countriesList({query: '?pagination=false'}));
            }
            if (isEmpty(this.cities)) {
                dependencies.push(this.citiesList({query: '?pagination=false'}));
            }

            await Promise.all(dependencies);
        } catch (error) {
            console.error(error)
        }
        
        this.loading = false;
    }

    // End Relation logic

    private async created() {
        try {
            await this.getRelations();
        } catch(error) {
           console.error(error);
        }
    }

}
